import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight, Sparkles, ArrowRight, Monitor, Printer, Scan, Wallet, Smartphone, CreditCard,
  ShieldCheck, Package, GraduationCap, Wrench, BadgeCheck, Phone, Check, Receipt,
} from "lucide-react";
import Reveal from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { BRAND, HARDWARE_IMG } from "../lib/brand";
import { ASSETS } from "../lib/brand";
import emailjs from "@emailjs/browser";

const SOLUTIONS = [
  { icon: Monitor, image: HARDWARE_IMG.touchPos, title: "Touch POS Systems", desc: "Industrial-grade touch POS terminals for fast, reliable billing.", features: ["Touchscreen interface", "Powerful processors", "Multiple ports", "Commercial durability"] },
  { icon: Printer, image: HARDWARE_IMG.labelPrinter, title: "Barcode Printers", desc: "High-speed receipt & label printers for billing counters.", features: ["203 DPI high resolution", "300m ribbon capacity", "Bluetooth and USB interface for easy connectivity", "Compact durable and easy to use"] },
  { icon: Scan, image: HARDWARE_IMG.barcodeScanner, title: "Barcode Scanning Solutions", desc: "Barcode hardware for inventory tracking.", features: ["Handheld & wireless scanners", "1D & 2D barcode scanning", "Fast identification", "Plug-and-play"] },
  { icon: Wallet, image: HARDWARE_IMG.cashDrawer, title: "Cash Handling & Displays", desc: "Essential billing counter peripherals.", features: ["Cash drawers", "Pole displays", "POS accessories", "Peripheral integration"] },
  { icon: Smartphone, image: HARDWARE_IMG.androidPos, title: "Portable Android POS", desc: "Compact mobile billing for field sales.", features: ["Android-based", "Portable", "Wireless", "Integrated printing"] },
  { icon: CreditCard, image: HARDWARE_IMG.Rfid, title: "RFID & Smart Cards", desc: "Smart identification and access control.", features: ["RFID cards", "MIFARE cards", "Membership cards", "Access control"] },
  {
  icon: Printer,
  image: HARDWARE_IMG.MobilePrinter,
  title: "Mobile Bluetooth Printer",
  desc: "Compact 58mm thermal printer with Bluetooth connectivity for mobile billing and receipt printing.",
  features: [
    "Bluetooth connectivity",
    "58mm,80mm and thermal printing",
    "2000mAh rechargeable battery",
    "6–8 hours continuous printing"
  ]
},
{
  icon: Printer,
  image: HARDWARE_IMG.ThermalPrinter,
  title: "Direct Thermal Receipt Printer",
  desc: "High-speed 72mm thermal receipt printer designed for retail, restaurants, and POS environments.",
  features: [
    "Up to 230mm/s print speed",
    "USB,USB + LAN ,USB + BLUETOOTH,USB + WIFI Models",
    "72mm print width",
    "Auto cutter support"
  ]
},
{
  icon: Monitor,
  image: HARDWARE_IMG.Kiosk,
  title: "Self-Service KIOSKs",
  desc: "Modular and versatile self-service terminals designed for modern business operations and scalable deployments.",
  features: [
    "Full-scope deployment",
    "Integrated solutions",
    "Technology deployment",
    "Operational optimization"
  ]
},

  
];

const CATEGORIES = [
  "Touch POS Terminals", "Thermal Receipt Printers", "Barcode Label Printers", "Barcode Scanners",
  "Portable Android POS Devices", "RFID Cards", "MIFARE Smart Cards", "Membership Cards", "Access Control Systems",
];

const CONSUMABLES = [
  { title: "Thermal Paper Rolls", desc: "High-quality receipt rolls for POS printers.", image: HARDWARE_IMG.thermalRoll },
  { title: "Barcode Labels", desc: "Durable labels for inventory and product tagging.", image: HARDWARE_IMG.barcodeLabels },
  { title: "Ribbon Rolls", desc: "Thermal transfer ribbons for barcode label printing.", image: HARDWARE_IMG.ribbonRoll },
  { title: "POS Accessories", desc: "Cables, adapters, peripherals, mounts, replacement parts.", image: HARDWARE_IMG.cables },
];

const RFID_APPS = ["Membership Programs", "Loyalty Cards", "Access Control Systems", "Employee Identification", "Visitor Management", "Amusement & Ticketing"];

const USPS = [
  { icon: Package, title: "Complete Software + Hardware Bundle" },
  { icon: Wrench, title: "Professional Installation Services" },
  { icon: GraduationCap, title: "Staff Training & Onboarding" },
  { icon: ShieldCheck, title: "Warranty & Technical Support" },
  { icon: BadgeCheck, title: "Genuine Products & Accessories" },
  { icon: Sparkles, title: "Long-Term After-Sales Assistance" },
];

const REQ_OPTIONS = ["Touch POS", "Thermal Printer", "Barcode Scanner", "Barcode Label Printer", "Portable Android POS", "RFID Solutions", "Other"];

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function Hardware() {
  const [form, setForm] = useState({ name: "", biz: "", phone: "", email: "", req: "", msg: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.biz || !form.phone || !form.req) {
      toast.error("Please fill in name, business, phone and requirement.");
      return;
    }
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      toast.error("Email service is not configured. Please try again later.");
      return;
    }

    setSubmitting(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: form.name,
          company: form.biz,
          phone: form.phone,
          email: form.email || "Not provided",
          message: `Requirement: ${form.req}\n\n${form.msg || "No message provided"}`,
          time: new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
        },
        { publicKey: EMAILJS_PUBLIC_KEY },
      );

      setSubmitting(false);
      toast.success("Quote request received! We'll get back within 24 hours.");
      setForm({ name: "", biz: "", phone: "", email: "", req: "", msg: "" });
    } catch (error) {
      setSubmitting(false);
      toast.error("Failed to send quote request. Please try again.");
    }
  };

  return (
    <div data-testid="hardware-page">
      <section className="relative gi-hero-gradient text-white pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 gi-mesh opacity-40" />
        <div className="absolute -top-20 -right-20 h-[420px] w-[420px] rounded-full bg-[#00B4D8]/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" /><span className="text-white">Hardware</span>
              </div>
              <span className="gi-pill-dark"><Sparkles className="h-3.5 w-3.5" /> Complete POS Hardware Ecosystem</span>
              <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-5 leading-tight">Complete POS Hardware Solutions</h1>
              <p className="text-white/75 mt-5 max-w-xl text-lg">
                We supply, install and support a full range of POS hardware and retail technology that integrates seamlessly with all types of software.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#quote" data-testid="hardware-quote-btn" className="gi-btn-primary">
                  Request a Quote <ArrowRight className="h-4 w-4" />
                </a>

                <Link to="/contact" className="gi-btn-ghost">
                  Talk to an Expert
                </Link>

                <button
                    className="gi-btn-ghost"
                    data-testid="zen-brochure-btn"
                    onClick={() => {
                      window.open("/brochures/Gravity- BrochureHW.pdf", "_blank");
                    }}
                  >
                    Download Brochure
                  </button>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div className="relative gi-float">
                <div className="absolute -inset-6 bg-gradient-to-br from-[#00B4D8]/40 to-transparent blur-3xl rounded-full" />
                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white border border-white/10">
                  <img src={ASSETS.hardware} alt="Gravityinc POS Hardware Solutions" className="w-full h-auto block" />
                </div>
              </div>
            </Reveal>
          </div>
            
        </div>
      </section>

      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-3 max-w-2xl">Everything Your Business Needs</h2>
            <p className="text-[#6B7280] max-w-2xl">From billing counters to warehouse operations, we provide every hardware component required to run a modern retail or distribution business.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SOLUTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 60}>
                <div className="gi-card gi-card-hover h-full overflow-hidden flex flex-col">
                  {s.image ? (
                    <div className="relative h-48 flex items-center justify-center p-6 overflow-hidden"
                        style={{
                          background: "radial-gradient(ellipse 80% 80% at center, #ffffff 0%, #e8f4ff 25%, #a8d4f5 50%, #3a8fd4 70%, #1a6bbf 85%, #1155a8 100%)"
                        }}
                      >
                        <img
                          src={s.image}
                          alt={s.title}
                          className="relative z-10 max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                          style={{ mixBlendMode: "multiply" }}
                        />
                        <div className="absolute top-3 left-3 h-9 w-9 rounded-lg bg-white shadow-sm border border-[#DDE6F0] text-[#1565C0] grid place-items-center">
                          <s.icon className="h-4 w-4" />
                        </div>
                      </div>  
                  ) : (
                    <div className="relative h-48 bg-gradient-to-br from-[#1565C0] to-[#00B4D8] flex items-center justify-center text-white">
                      <s.icon className="h-16 w-16 opacity-90" />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-display font-semibold text-lg mb-2">{s.title}</h3>
                    <p className="text-sm text-[#6B7280] mb-4">{s.desc}</p>
                    <ul className="space-y-1.5 text-xs text-[#1A1A2E]/80 mt-auto">
                      {s.features.map((f) => (
                        <li key={f} className="flex items-start gap-2"><Check className="h-3.5 w-3.5 text-[#10B981] mt-0.5 shrink-0" />{f}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      

      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-3">Consumables & Operational Essentials</h2>
            <p className="text-[#6B7280] max-w-2xl">We supply genuine, long-lasting consumables to keep your counter running.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CONSUMABLES.map((c, i) => (
              <Reveal key={c.title} delay={i * 60}>
                <div className="gi-card gi-card-hover h-full overflow-hidden flex flex-col">
                  {c.image ? (
                    <div
                      className="relative h-40 flex items-center justify-center p-5 overflow-hidden"
                      style={{
                        background:
                          "radial-gradient(ellipse 80% 80% at center, #ffffff 0%, #e8f4ff 25%, #a8d4f5 50%, #3a8fd4 70%, #1a6bbf 85%, #1155a8 100%)",
                      }}
                    >
                      <img
                        src={c.image}
                        alt={c.title}
                        className="relative z-10 max-h-full max-w-full object-contain"
                        style={{ mixBlendMode: "multiply" }}
                      />
                    </div>
                  ) : (
                    <div className="relative h-40 bg-gradient-to-br from-[#00B4D8] to-[#1565C0] grid place-items-center text-white">
                      <Package className="h-12 w-12 opacity-90" />
                    </div>
                  )}
                  <div className="p-5">
                    <h4 className="font-display font-semibold text-base">{c.title}</h4>
                    <p className="text-xs text-[#6B7280] mt-2">{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      

      

      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-3">Why Businesses Choose Gravityinc Hardware</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {USPS.map((u, i) => (
              <Reveal key={u.title} delay={i * 50}>
                <div className="gi-card gi-card-hover p-6 flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#F77F00] to-[#FF9A3C] text-white grid place-items-center shrink-0">
                    <u.icon className="h-5 w-5" />
                  </div>
                  <div className="font-display font-semibold text-base">{u.title}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="quote" className="bg-[#F5F8FF] gi-section">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <span className="font-mono text-xs uppercase tracking-widest text-[#1565C0]">Get a Quote</span>
              <h2 className="font-display font-bold text-3xl md:text-4xl mt-2">Looking for Pricing or Hardware Recommendations?</h2>
              <p className="text-[#6B7280] mt-3 max-w-2xl mx-auto">Tell us a bit about your business and we&apos;ll send a tailored quote within 24 hours.</p>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <form data-testid="quote-form" onSubmit={onSubmit} className="gi-card p-7 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name *" testId="quote-name"><Input value={form.name} onChange={onChange("name")} placeholder="Your name" /></Field>
              <Field label="Business Name *" testId="quote-biz"><Input value={form.biz} onChange={onChange("biz")} placeholder="Business name" /></Field>
              <Field label="Phone Number *" testId="quote-phone"><Input value={form.phone} onChange={onChange("phone")} placeholder="+91 99959 89843" /></Field>
              <Field label="Email Address" testId="quote-email"><Input type="email" value={form.email} onChange={onChange("email")} placeholder="you@business.com" /></Field>
              <Field label="Hardware Requirement *" testId="quote-req" className="md:col-span-2">
                <Select value={form.req} onValueChange={onChange("req")}>
                  <SelectTrigger data-testid="quote-req-trigger"><SelectValue placeholder="Select a category" /></SelectTrigger>
                  <SelectContent>
                    {REQ_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Message" testId="quote-msg" className="md:col-span-2">
                <Textarea rows={4} value={form.msg} onChange={onChange("msg")} placeholder="Tell us about your store and hardware needs…" />
              </Field>
              <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-[#6B7280]">We&apos;ll respond within 24 hours.</p>
                <button type="submit" data-testid="quote-submit" disabled={submitting} className="gi-btn-primary disabled:opacity-70">
                  {submitting ? "Sending…" : "Request a Quote"} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="gi-orange-gradient">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 flex flex-col md:flex-row items-center justify-between gap-6 text-white">
          <div>
            <h3 className="font-display font-bold text-2xl md:text-3xl">Need the Right Hardware for Your Business?</h3>
            <p className="text-white/90 mt-2 max-w-2xl">Our team will recommend the ideal POS setup based on your industry, store size and operational requirements.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a href="#quote" className="bg-white text-[#F77F00] font-semibold px-6 py-3 rounded-lg hover:scale-105 transition inline-flex items-center gap-2">Get a Quote <ArrowRight className="h-4 w-4" /></a>
            <a href={`tel:${BRAND.phone1}`} className="border-2 border-white text-white font-semibold px-6 py-3 rounded-lg hover:bg-white hover:text-[#F77F00] transition inline-flex items-center gap-2"><Phone className="h-4 w-4" /> Call {BRAND.phone1}</a>
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, children, testId, className = "" }) => (
  <div className={className}>
    <Label className="text-xs uppercase tracking-wider font-medium text-[#6B7280] mb-2 block">{label}</Label>
    <div data-testid={testId}>{children}</div>
  </div>
);
